/**
 * Two-step sign-in: after the password, a code from an authenticator app (Google
 * Authenticator, Aegis, 1Password...). Turning it on shows a QR code and the secret to type,
 * then asks a first code, then shows ten recovery codes once (each signs in once when the
 * phone is lost). Turning it off, or making new recovery codes, asks a code again.
 */
import { For, Show, createSignal, type JSX } from "solid-js";
import type { SecondStepSetup } from "@protocol/SecondStepSetup";
import { downloadText } from "../shared/download";
import { QrCode } from "../shared/QrCode";
import { toast } from "../shared/toast";
import { usePages } from "./context";

/** The two-step section. */
export function AccountSecondStep(): JSX.Element {
  const { accounts, server } = usePages();
  const [setup, setSetup] = createSignal<SecondStepSetup | null>(null);
  const [code, setCode] = createSignal("");
  const [recovery, setRecovery] = createSignal<string[]>([]);
  const on = () => accounts.me(server)?.second_step === true;

  const start = async () => setSetup(await accounts.call(server, (a) => a.startSecondStep()));
  const confirm = async () => {
    const codes = await accounts.call(server, (a) => a.confirmSecondStep(code()));
    if (!codes) return;
    setSetup(null);
    setCode("");
    setRecovery(codes.codes);
    toast("Two-step sign-in is on.");
    await accounts.refresh(server);
  };
  const stop = async () => {
    const done = await accounts.call(server, (a) => a.stopSecondStep(code()).then(() => true));
    if (!done) return;
    setCode("");
    setRecovery([]);
    toast("Two-step sign-in is off.");
    await accounts.refresh(server);
  };
  const renew = async () => {
    const codes = await accounts.call(server, (a) => a.newRecoveryCodes(code()));
    if (!codes) return;
    setCode("");
    setRecovery(codes.codes);
  };

  return (
    <section class="team-section" id="account-second-step" data-second-step={on() ? "on" : "off"}>
      <h3>Two-step sign-in</h3>
      <Show
        when={on()}
        fallback={
          <Show
            when={setup()}
            fallback={
              <>
                <p class="small muted">After your password, ask for a code from an app on your phone: a stolen password alone no longer opens your account.</p>
                <button type="button" class="btn primary" id="second-step-start" onClick={() => void start()}>
                  Turn on
                </button>
              </>
            }
          >
            {(s) => (
              <div class="second-step-setup">
                <p class="small">1. Scan this with your authenticator app, or type the key.</p>
                <QrCode id="second-step-qr" text={s().uri} />
                <p class="mono small" id="second-step-secret">
                  {s().secret.replace(/(.{4})/g, "$1 ").trim()}
                </p>
                <p class="small">2. Type the code the app shows now.</p>
                <form
                  class="row-inline"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void confirm();
                  }}
                >
                  <input class="field mono" id="second-step-code" inputmode="numeric" autocomplete="one-time-code" value={code()} onInput={(e) => setCode(e.currentTarget.value)} />
                  <button type="submit" class="btn primary" id="second-step-confirm">
                    Turn on
                  </button>
                  <button type="button" class="btn" onClick={() => setSetup(null)}>
                    Cancel
                  </button>
                </form>
              </div>
            )}
          </Show>
        }
      >
        <p class="small">On: signing in asks a code from your app after the password.</p>
        <form class="row-inline" onSubmit={(e) => e.preventDefault()}>
          <input class="field mono" id="second-step-off-code" placeholder="code or recovery code" inputmode="numeric" value={code()} onInput={(e) => setCode(e.currentTarget.value)} />
          <button type="button" class="btn" id="second-step-recovery" onClick={() => void renew()}>
            New recovery codes
          </button>
          <button type="button" class="btn" id="second-step-off" onClick={() => void stop()}>
            Turn off
          </button>
        </form>
      </Show>
      <Show when={recovery().length > 0}>
        <div class="recovery-codes" id="recovery-codes">
          <p class="small">
            <b>Recovery codes.</b> Each signs in once without your phone. Keep them somewhere safe: they are shown only now.
          </p>
          <ul class="mono">
            <For each={recovery()}>{(c) => <li data-recovery-code>{c}</li>}</For>
          </ul>
          <button type="button" class="btn" id="recovery-download" onClick={() => downloadText("knodal-recovery-codes.txt", `Knodal recovery codes for ${accounts.me(server)?.account.email ?? ""} on ${server}\n\n${recovery().join("\n")}\n`)}>
            Download
          </button>
        </div>
      </Show>
    </section>
  );
}
